import { store } from 'redux/store';

import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { getDocumentBlob } from 'modules/_shared/components/CKEditor/utils/getDocumentBlob';
import { updateDocumentWithValues } from 'modules/_shared/components/EditorWYSIWYG/helpers';
import tags from 'modules/_shared/components/EditorWYSIWYG/tags';
import { addAlert } from 'modules/_shared/redux/alertActions';
import { getDocument } from 'modules/documents/redux/documentActions';

import { Operation, Partner, Society } from 'types';
import { downloadBlob } from 'utils/downloadBlob';
import errorSwal from 'utils/errorSwal';
import i18n from 'i18n/config';

export enum TaxIncomeDocumentShareholdersType {
  RESIDENTS = 'RESIDENTS', // eslint-disable-line no-unused-vars
  NON_RESIDENTS = 'NON_RESIDENTS', // eslint-disable-line no-unused-vars
  ALL = 'ALL', // eslint-disable-line no-unused-vars
}

type GenerateTaxIncomeDocumentProps = {
  partner: Partner | null;
  society: Society;
  operation: Operation;
  shareholdersType: TaxIncomeDocumentShareholdersType;
  fiscalYear: number;
};

const COMPANY_PERCENT_RETENTION = 19;

export const generateTaxIncomeDocument = async ({
  partner,
  society,
  operation,
  shareholdersType,
  fiscalYear,
}: GenerateTaxIncomeDocumentProps) => {
  const templateId = society?.templates?.tax2025;

  if (!templateId) {
    errorSwal(
      i18n.t('ErrorNoTaxTemplateFound'),
      i18n.t('ErrorNoTaxTemplateFoundDescription')
    );
    return;
  }

  const totalShareholders = {
    [TaxIncomeDocumentShareholdersType.ALL]:
      operation.dividend?.totalShareholders || 0,
    [TaxIncomeDocumentShareholdersType.RESIDENTS]:
      operation.dividend?.totalShareholdersResidents || 0,
    [TaxIncomeDocumentShareholdersType.NON_RESIDENTS]:
      operation.dividend?.totalShareholdersNonResidents || 0,
  };

  const totalAmount = {
    [TaxIncomeDocumentShareholdersType.ALL]:
      operation.dividend?.totalAmount || 0,
    [TaxIncomeDocumentShareholdersType.RESIDENTS]:
      operation.dividend?.totalAmountResidents || 0,
    [TaxIncomeDocumentShareholdersType.NON_RESIDENTS]:
      operation.dividend?.totalAmountNonResidents || 0,
  };

  try {
    const template = await store.dispatch(getDocument(templateId));

    const dividend = {
      totalShareholders: totalShareholders[shareholdersType] || 0,
      totalAmount: totalAmount[shareholdersType] || 0,
      totalNetAmount:
        totalAmount[shareholdersType] * (COMPANY_PERCENT_RETENTION / 100),
    };

    const documentUpdated = updateDocumentWithValues(
      template.editorModel,
      tags({
        partner,
        society,
        dividend,
        operation: { operationDate: new Date() },
      })
    );

    // This custom CSS is needed to match the 192-2024-GIP document layout
    const exportContent = `
    <html>
    <head>
        <style>
            @page {
                margin: 0;
            }
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                margin: 20px;
            }
            .ck-table-resized,
            .ck-editor__nested-editable {
                line-height: 0.88 !important
            }
            .ck-table-resized p {
                margin-top: 0 !important;
                margin-bottom: 0 !important;
            }
            .ck-content {
                background-color: #fedbbb !important
            }
        </style>
    </head>
    <body>
        ${documentUpdated.html}
    </body>
    </html>
    `;

    const templateBlob = await getDocumentBlob({
      html: exportContent,
      ignoreContentPadding: true,
    });

    const formattedDate = new Date().toISOString().split('T')[0];
    const fileName = `${
      society?.name
    }_${formattedDate}_193-${fiscalYear.toString()}.pdf`;

    downloadBlob(templateBlob, fileName);
    store.dispatch(addAlert(alertBodyTypes.DOCUMENT_GENERATED));
  } catch (error) {
    store.dispatch(addAlert(alertBodyTypes.ERROR_GENERATING_DOCUMENT));
  }
};
